import notificationHelpers from '@satellite/helpers/notifications';
import { isFormValid } from '@satellite/plugins/util';
import { $t } from '@satellite/plugins/translator';

/**
 * Global utility mixin
 * @mixin
 * @displayName Utility Mixin
 */
export default {
  data() {
    return {
      $globalLoading: false
    };
  },
  methods: {
    waitForElementRender(documentMethod, selector, vueContext, callback) {
      const interval = setInterval(() => {
        const el = document[documentMethod](`${selector}`);
        if (el) {
          callback(el, vueContext);
          clearInterval(interval);
        }
      }, 10);
    },
    makeRoutePathByName: function (name, query, params) {
      let routeObj = { name: name };

      if (query) {
        routeObj.query = query;
      }

      if (params) {
        routeObj.params = params;
      }

      return this.$router.resolve(routeObj).href;
    },
    notify(message, type = 'success', options = {}) {
      options = {
        ...{
          duration: 5000,
          group: 'bottom-notifications',
          clean: false,
          actionText: null,
          actionFn: null,
          title: null
        },
        ...options
      };

      this.$notify({
        group: options.group,
        title: this.novaCore.sanitizeInput(this.novaCore.upperFirst(options.title ?? type)),
        text: this.novaCore.sanitizeInput(message),
        duration: options.duration,
        data: { ...notificationHelpers.getNotificationConfig(type), ...options },
        clean: options.clean
      });
    },
    clearNotifications(group = 'app-notifications') {
      this.notify('', '', 0, group, true);
    },
    getValidations(error) {
      if (!error.response || !error.response.data || !error.response.data.errors) {
        return {};
      }

      return error.response.data.errors;
    },
    navigate(name, params) {
      this.$router.push({ name, params });
    },
    // Shake an element to bring attention to it
    shakeElement(refKey = '') {
      let ref = this.$refs[refKey];

      if (!ref) {
        console.log('shakeElement: Ref element does not exist');
        return;
      }

      ref.$el.classList.add('shake-element');
      setTimeout(() => {
        ref.$el.classList.remove('shake-element');
      }, 1000);
    },
    handleAppointmentQueryString(currentPath, appointmentId, routeAction = 'push') {
      if (currentPath === '/appointments') {
        this.$router[routeAction]({ query: { appointmentId } });
      } else {
        this.$router[routeAction]({ path: '/appointments', query: { appointmentId } });
      }
    },
    setDirtyStatus(isDirty, msg = null) {
      this.$route.meta.isDirty = isDirty;
      window.onbeforeunload = () =>
        isDirty
          ? msg || $t('global.messages.areYouSureYouWantToLeaveYouMayHaveUnsavedChanges')
          : undefined;
    },
    async checkDirtyConfirmation(msg = null) {
      let shouldLeave = await this.$confirm(
        msg || $t('global.messages.youMayHaveUnsavedChangesAreYouSureYouWantToLeaveThisPage'),
        {
          color: 'warning',
          buttonTrueText: $t('global.phrases.leaveWithoutSaving')
        }
      ).then(confirmed => confirmed);

      if (shouldLeave) {
        this.setDirtyStatus(false);
      }

      return shouldLeave;
    },
    copyValueToClipboard(value) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(value);
        this.notify(`${$t('global.phrases.copiedToClipboard')}!`);
      } else {
        this.notify($t('global.messages.browserDoesNotSupportCopyFunction'), 'error');
      }
    },
    makeAppointmentCreatorType(user) {
      if (!user) {
        return $t('global.phrases.unknownUser');
      }
      return this.novaCore.isCarrierUser(user)
        ? $t('global.specialtyTerms.carriers')
        : $t('global.specialtyTerms.warehouses');
    },
    async $copyContent(
      contentToCopy,
      successMessage = $t('global.phrases.copiedToClipboard'),
      successCallback
    ) {
      // Permissions API is NOT supported on firefox
      // ref.: https://developer.mozilla.org/en-US/docs/Web/API/Clipboard_API#security_considerations

      try {
        await navigator.clipboard.writeText(contentToCopy);
        this.notify(successMessage, 'success');
        if (successCallback) {
          successCallback();
        }
      } catch {
        this.notify(
          $t(
            'global.messages.permissionToAccessClipboardWasDeniedToEnableAccessUpdateYourBrowserSettings'
          ) +
            `<br />${$t(
              'global.messages.alternativelyYouCanManuallyCopy'
            )}: <br />${contentToCopy}`,
          'warning'
        );
      }
    },
    async $isFormValid(formRef, scrollToError = true) {
      return isFormValid(formRef, scrollToError);
    }
  }
};
